import React from "react";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { submitContactForm } from "../../lib/sanity";
import "./ContactForm.css";

const Field = ({ label, name, placeholder, required = false, type = "text" }) => (
  <label className="contact-form__field">
    <span>{label}{required && " *"}</span>
    <input name={name} type={type} placeholder={placeholder || label} required={required} />
  </label>
);

const SelectField = ({ label, name, options, required = false }) => (
  <label className="contact-form__field">
    <span>{label}{required && " *"}</span>
    <select name={name} required={required} defaultValue="">
      <option value="" disabled>{label}</option>
      {options.map((option) => <option value={option} key={option}>{option}</option>)}
    </select>
  </label>
);

const ContactForm = () => {
  const [status, setStatus] = React.useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [showThankYou, setShowThankYou] = React.useState(false);
  const [enquiryType, setEnquiryType] = React.useState("Request Quote");

  React.useEffect(() => {
    if (!showThankYou) return undefined;
    const timeout = window.setTimeout(() => setShowThankYou(false), 5000);
    return () => window.clearTimeout(timeout);
  }, [showThankYou]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const values = Object.fromEntries(new FormData(event.currentTarget).entries());
      await submitContactForm(values);
      form.reset();
      setSelectedFile(null);
      setEnquiryType("Request Quote");
      setStatus({ type: "", message: "" });
      setShowThankYou(true);
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
  <section className="contact-form-section">
    <div className="contact-form__intro">
      <p>Let&apos;s work together</p>
      <h2>Tell us about your requirement.</h2>
      <span>Share your company and contact details with our team. We&apos;ll get back to you shortly.</span>
    </div>

    {showThankYou ? (
    <div className="contact-form contact-form__thank-you" role="status">
      <strong>Thank you for filling out the form.</strong>
      <span>Your request has been sent successfully.</span>
    </div>
    ) : (
    <form className="contact-form" onSubmit={handleSubmit}>
      {status.message && <p className={`contact-form__status contact-form__status--${status.type}`} role="status">{status.message}</p>}
      <div className="contact-form__enquiry-type" role="tablist" aria-label="Enquiry type">
        <input type="hidden" name="enquiryType" value={enquiryType} />
        <div className="contact-form__tabs">
          {[
            ["Request Quote", "Request a quote"],
            ["Job", "Job enquiry"],
            ["General Enquiry", "General enquiry"],
          ].map(([value, label]) => (
            <button
              type="button"
              role="tab"
              aria-selected={enquiryType === value}
              className={enquiryType === value ? "is-active" : ""}
              key={value}
              onClick={() => setEnquiryType(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {enquiryType === "Job" && (
        <>
          <h3>Application details</h3>
          <div className="contact-form__grid contact-form__grid--two">
            <Field label="First name" name="firstName" required />
            <Field label="Last name" name="lastName" required />
            <Field label="E-Mail" name="email" type="email" required />
            <Field label="Phone" name="phone" type="tel" required />
            <SelectField label="Area of interest" name="department" options={["Engineering", "Production", "Quality", "Purchase", "Administration", "Other"]} required />
            <Field label="Position applying for" name="position" required />
          </div>
          <label className="contact-form__field contact-form__field--message">
            <span>Message</span>
            <textarea name="request" placeholder="Tell us about your experience and the role you are interested in" rows="5" required />
          </label>
          <label className="contact-upload">
            <CloudArrowUpIcon />
            <span><strong>Drag &amp; Drop or Click to upload your resume</strong><small>File size limit: 3MB<br />Accepted file types: PDF, DOC, DOCX</small></span>
            <input type="file" name="attachment" accept="application/pdf,.doc,.docx" onChange={(event) => setSelectedFile(event.target.files[0] || null)} />
          </label>
          {selectedFile && <p className="contact-upload__file" aria-live="polite"><strong>Uploaded resume:</strong> {selectedFile.name}</p>}
        </>
      )}

      {enquiryType === "Request Quote" && (
        <>
          <h3>Company details</h3>
          <div className="contact-form__grid contact-form__grid--two">
            <Field label="Company" name="company" required />
            <SelectField label="Type of Company" name="companyType" options={["Manufacturer", "Supplier", "Distributor", "Service provider", "Other"]} required />
            <Field label="Website" name="website" type="url" />
          </div>
          <h3>Contact person</h3>
          <div className="contact-form__grid contact-form__grid--two">
            <Field label="First name" name="firstName" required />
            <Field label="Last name" name="lastName" required />
            <Field label="E-Mail" name="email" type="email" required />
            <Field label="Phone" name="phone" type="tel" required />
          </div>
          <label className="contact-form__field contact-form__field--message">
            <span>Tell us about your requirement</span>
            <textarea name="request" placeholder="Products, quantities, materials, drawings or other project details" rows="5" required />
          </label>
          <label className="contact-upload">
            <CloudArrowUpIcon />
            <span><strong>Drag &amp; Drop or Click to upload files</strong><small>File size limit: 3MB<br />Accepted file types: PDF, JPEG, PNG, WEBP</small></span>
            <input type="file" name="attachment" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setSelectedFile(event.target.files[0] || null)} />
          </label>
          {selectedFile && <p className="contact-upload__file" aria-live="polite"><strong>Uploaded document:</strong> {selectedFile.name}</p>}
        </>
      )}

      {enquiryType === "General Enquiry" && (
        <>
          <h3>Your details</h3>
          <div className="contact-form__grid contact-form__grid--two">
            <Field label="First name" name="firstName" required />
            <Field label="Last name" name="lastName" required />
            <Field label="E-Mail" name="email" type="email" required />
            <Field label="Phone" name="phone" type="tel" />
            <Field label="Company" name="company" />
          </div>
          <label className="contact-form__field contact-form__field--message">
            <span>Message</span>
            <textarea name="request" placeholder="How can we help you?" rows="5" required />
          </label>
        </>
      )}

      {!enquiryType && <p className="contact-form__hint">Choose an enquiry type to see the relevant form.</p>}
      {enquiryType && (
        <>
          <label className="contact-form__privacy"><input type="checkbox" name="privacyAccepted" required /> <span>I accept the Privacy Disclaimer. *</span></label>
          <label className="contact-form__captcha"><input type="checkbox" /> <span><strong>I am human</strong><small>Friendly Captcha</small></span><b>•••</b></label>
          <button type="submit" className="contact-form__submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Send request"} <span>→</span>
          </button>
        </>
      )}
    </form>
    )}
  </section>
  );
};

export default ContactForm;
